// Helpers for a user's data-rights requests (access/export, erasure).
// They take `db` as a parameter so this file never imports AuthContext
// (which would create a circular import).

import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";

// Collections that store records linked to a user by a `userId` field.
export const LINKED_COLLECTIONS = ["feedback", "waitlist"];

// Everything the browser keeps for the user. The cookie-choice record is kept
// on purpose: it is not account data and avoids re-asking right after deletion.
const KEEP_KEYS = ["noryq-consent"];

export function readLocalUserData() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("noryq-") && !KEEP_KEYS.includes(key)) {
        out[key] = localStorage.getItem(key);
      }
    }
  } catch (_) {}
  return out;
}

export function clearLocalUserData() {
  try {
    Object.keys(readLocalUserData()).forEach((k) => localStorage.removeItem(k));
  } catch (_) {}
}

// Firestore says "Missing or insufficient permissions" when the published
// security rules do not allow a request. For a person using the app that message
// is useless, so turn it into something they can act on.
export function friendlyPermissionError(e) {
  if (e && (e.code === "permission-denied" || /insufficient permissions/i.test(e.message || ""))) {
    const err = new Error(
      "We could not reach your stored records because of a server permission problem. Nothing was deleted and your account is unchanged. Please email noryq.official@gmail.com and we will complete this for you.",
    );
    err.code = "permission-denied";
    return err;
  }
  return e;
}

async function fetchLinked(db, collectionName, uid) {
  const snap = await getDocs(
    query(collection(db, collectionName), where("userId", "==", uid)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Build a complete, human-readable copy of what we hold about this user.
export async function exportUserData(db, user) {
  const profileSnap = await getDoc(doc(db, "users", user.uid));
  const linked = {};
  try {
    for (const name of LINKED_COLLECTIONS) {
      linked[name] = await fetchLinked(db, name, user.uid);
    }
  } catch (e) {
    throw friendlyPermissionError(e);
  }
  let payments = [];
  try {
    payments = await fetchLinked(db, "orders", user.uid);
  } catch (_) {
    payments = []; // no access yet (rules not republished) or none exist
  }
  return {
    exportedAt: new Date().toISOString(),
    about:
      "A copy of the personal data NORYQ holds about you. Data in other places (e.g. Google Analytics, if you allowed it) is held by those providers.",
    account: {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified,
      createdAt: user.metadata?.creationTime || null,
      lastSignInAt: user.metadata?.lastSignInTime || null,
      signInMethods: (user.providerData || []).map((p) => p.providerId),
    },
    profile: profileSnap.exists() ? profileSnap.data() : null,
    feedback: linked.feedback,
    premiumWaitlist: linked.waitlist,
    payments,
    savedInThisBrowser: readLocalUserData(),
  };
}

// Delete every record in the given collections linked to this user.
// Returns how many documents were removed.
export async function deleteLinkedRecords(db, uid, collections = LINKED_COLLECTIONS) {
  let removed = 0;
  try {
    for (const name of collections) {
      const snap = await getDocs(
        query(collection(db, name), where("userId", "==", uid)),
      );
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      removed += snap.size;
    }
  } catch (e) {
    throw friendlyPermissionError(e);
  }
  return removed;
}

export function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
